import React, { useEffect, useState } from 'react';

export interface EdgeTypeChoice {
  category: 'procedural' | 'structural';
  subtype: string;
  label: string;
  instrumentPolarity?: 'none' | 'not';
  instrumentDisplayType?: 'instrument' | 'instrument-condition' | 'instrument-event';
}

type InstrumentPolarity = 'none' | 'not';
type InstrumentDisplayType = 'instrument' | 'instrument-condition' | 'instrument-event';

const DEFAULT_INSTRUMENT_POLARITY: InstrumentPolarity = 'none';
const DEFAULT_INSTRUMENT_DISPLAY_TYPE: InstrumentDisplayType = 'instrument';

interface EdgeTypeDialogProps {
  visible: boolean;
  sourceLabel: string;
  targetLabel: string;
  sourceNodeType: string;
  targetNodeType?: string;
  isProcessSubGraph?: boolean;
  onConfirm: (choice: EdgeTypeChoice) => void;
  onCancel: () => void;
}

const STRUCTURAL_TYPES: EdgeTypeChoice[] = [
  { category: 'structural', subtype: 'exhibition-characterization', label: '展示特征' },
];

const PROCEDURAL_TYPES: EdgeTypeChoice[] = [
  { category: 'procedural', subtype: 'generation', label: '生成' },
  { category: 'procedural', subtype: 'effect', label: '影响' },
  { category: 'procedural', subtype: 'agent-enablement', label: 'Agent 使能' },
];

const INVOCATION_TYPE: EdgeTypeChoice = {
  category: 'procedural',
  subtype: 'invocation',
  label: '调用',
};

// process 子图中非 process↔process 调用时展示的类型
const PROCESS_OTHER_TYPES: EdgeTypeChoice[] = [
  { category: 'procedural', subtype: 'instrument', label: '使能' },
  { category: 'procedural', subtype: 'consumption', label: '消耗' },
  { category: 'procedural', subtype: 'effect', label: '影响' },
];

// SVG 图标：三角形嵌套三角形（Exhibition-Characterization）
const ExhibitionIcon: React.FC = () => (
  <svg width="48" height="36" viewBox="0 0 48 36">
    <path d="M24 4 L40 30 L8 30 Z" fill="none" stroke="#5a7fa5" strokeWidth="2" />
    <path d="M24 14 L32 28 L16 28 Z" fill="#5a7fa5" stroke="#5a7fa5" strokeWidth="1.5" />
  </svg>
);

// SVG 图标：线 + 空心圆点（Instrument）
const InstrumentIcon: React.FC<{
  polarity?: InstrumentPolarity;
  displayType?: InstrumentDisplayType;
}> = ({
  polarity = DEFAULT_INSTRUMENT_POLARITY,
  displayType = DEFAULT_INSTRUMENT_DISPLAY_TYPE,
}) => (
  <svg width="64" height="36" viewBox="0 0 64 36">
    <line x1="6" y1="18" x2="42" y2="18" stroke="#5a7fa5" strokeWidth="2" />
    <circle cx="48" cy="18" r="6" fill="#ffffff" stroke="#5a7fa5" strokeWidth="2" />
    {polarity === 'not' && (
      <path d="M50 6 L58 6 L58 12" fill="none" stroke="#5a7fa5" strokeWidth="2" strokeLinecap="round" />
    )}
    {displayType === 'instrument-condition' && (
      <text x="26" y="12" fontSize="9" fontWeight="bold" fill="#5a7fa5" textAnchor="middle">c</text>
    )}
    {displayType === 'instrument-event' && (
      <text x="26" y="12" fontSize="9" fontWeight="bold" fill="#5a7fa5" textAnchor="middle">e</text>
    )}
  </svg>
);

// SVG 图标：空心箭头 →（Consumption）
const ConsumptionIcon: React.FC = () => (
  <svg width="48" height="36" viewBox="0 0 48 36">
    <line x1="4" y1="18" x2="30" y2="18" stroke="#5a7fa5" strokeWidth="2" />
    <path d="M30 10 L42 18 L30 26 L34 18 Z" fill="#ffffff" stroke="#5a7fa5" strokeWidth="2" />
  </svg>
);

// SVG 图标：双箭头 ↔（Effect）
const EffectIcon: React.FC = () => (
  <svg width="56" height="36" viewBox="-4 0 56 36">
    <line x1="8" y1="18" x2="40" y2="18" stroke="#5a7fa5" strokeWidth="2" />
    <path d="M8 10 L-2 18 L8 26 L4 18 Z" fill="#5a7fa5" stroke="#5a7fa5" strokeWidth="1.5" />
    <path d="M40 10 L50 18 L40 26 L44 18 Z" fill="#5a7fa5" stroke="#5a7fa5" strokeWidth="1.5" />
  </svg>
);

// SVG 图标：菱形箭头 →（生成）- 目标端空心箭头
const GenerationIcon: React.FC = () => (
  <svg width="48" height="36" viewBox="0 0 48 36">
    <line x1="4" y1="18" x2="30" y2="18" stroke="#5a7fa5" strokeWidth="2" />
    <path d="M30 10 L42 18 L30 26 L34 18 Z" fill="#ffffff" stroke="#5a7fa5" strokeWidth="2" />
    <circle cx="4" cy="18" r="3" fill="#5a7fa5" />
  </svg>
);

// SVG 图标：实心圆点 + ¬C 标识（Agent 使能关系）
const AgentEnablementIcon: React.FC = () => (
  <svg width="48" height="36" viewBox="0 0 48 36">
    <line x1="4" y1="18" x2="36" y2="18" stroke="#5a7fa5" strokeWidth="2" />
    <circle cx="40" cy="18" r="5" fill="#5a7fa5" stroke="#5a7fa5" strokeWidth="1.5" />
    <text x="30" y="10" fontSize="8" fontWeight="bold" fill="#5a7fa5" textAnchor="middle">¬C</text>
  </svg>
);

// SVG 图标：调用（过程间调用）— Z 字形单箭头
const InvocationIcon: React.FC = () => (
  <svg width="68" height="36" viewBox="0 0 98 36">
    <polyline
      points="0,6 62,6 40,22 84,22"
      fill="none"
      stroke="#5a7fa5"
      strokeWidth="2"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
    <path d="M12 0 L0 6 L12 12 L8 6 Z" fill="#5a7fa5" stroke="#5a7fa5" strokeWidth="1.5" />
  </svg>
);

const EdgeTypeDialog: React.FC<EdgeTypeDialogProps> = ({
  visible,
  sourceLabel,
  targetLabel,
  sourceNodeType,
  targetNodeType,
  isProcessSubGraph,
  onConfirm,
  onCancel,
}) => {
  const [selectedType, setSelectedType] = useState<EdgeTypeChoice | null>(null);
  const [instrumentPolarity, setInstrumentPolarity] = useState<InstrumentPolarity>(DEFAULT_INSTRUMENT_POLARITY);
  const [instrumentDisplayType, setInstrumentDisplayType] = useState<InstrumentDisplayType>(
    DEFAULT_INSTRUMENT_DISPLAY_TYPE,
  );

  // process 子图中：只有 process↔process 才显示 Z 字形调用（invocation）
  const isProcessInvocation =
    isProcessSubGraph &&
    sourceNodeType === 'process' &&
    (targetNodeType || sourceNodeType) === 'process';

  const structuralTypes = isProcessInvocation ? [] : STRUCTURAL_TYPES;
  const proceduralTypes = isProcessInvocation
    ? [INVOCATION_TYPE]
    : isProcessSubGraph
      ? PROCESS_OTHER_TYPES
      : PROCEDURAL_TYPES;

  useEffect(() => {
    if (!visible) {
      setSelectedType(null);
      setInstrumentPolarity(DEFAULT_INSTRUMENT_POLARITY);
      setInstrumentDisplayType(DEFAULT_INSTRUMENT_DISPLAY_TYPE);
      return;
    }
    if (isProcessInvocation) {
      setSelectedType(INVOCATION_TYPE);
    }
  }, [visible, isProcessInvocation]);

  if (!visible) return null;

  const handleConfirm = () => {
    if (selectedType) {
      if (selectedType.subtype === 'instrument') {
        onConfirm({
          ...selectedType,
          instrumentPolarity,
          instrumentDisplayType,
        });
      } else {
        onConfirm(selectedType);
      }
      setSelectedType(null);
    }
  };

  const handleCancel = () => {
    setSelectedType(null);
    setInstrumentPolarity(DEFAULT_INSTRUMENT_POLARITY);
    setInstrumentDisplayType(DEFAULT_INSTRUMENT_DISPLAY_TYPE);
    onCancel();
  };

  const renderOption = (type: EdgeTypeChoice) => {
    const isSelected =
      selectedType?.category === type.category && selectedType?.subtype === type.subtype;
    const renderIcon = () => {
      switch (type.subtype) {
        case 'exhibition-characterization':
          return <ExhibitionIcon />;
        case 'instrument':
          return (
            <InstrumentIcon
              polarity={isSelected ? instrumentPolarity : DEFAULT_INSTRUMENT_POLARITY}
              displayType={isSelected ? instrumentDisplayType : DEFAULT_INSTRUMENT_DISPLAY_TYPE}
            />
          );
        case 'consumption':
          return <ConsumptionIcon />;
        case 'generation':
          return <GenerationIcon />;
        case 'agent-enablement':
          return <AgentEnablementIcon />;
        case 'invocation':
          return <InvocationIcon />;
        case 'effect':
          return <EffectIcon />;
        default:
          return <EffectIcon />;
      }
    };

    const handleSelect = () => {
      if (type.subtype === 'instrument') {
        setSelectedType({
          ...type,
          instrumentPolarity,
          instrumentDisplayType,
        });
        return;
      }
      setSelectedType(type);
    };

    return (
      <button
        key={type.subtype}
        onClick={handleSelect}
        style={{
          padding: '12px 20px',
          border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
          borderRadius: 6,
          background: isSelected ? '#e6f7ff' : '#fff',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          minWidth: 100,
          transition: 'all 0.2s',
        }}
      >
        {renderIcon()}
        <span style={{ fontSize: 12, color: '#333' }}>{type.label}</span>
      </button>
    );
  };

  const isInstrumentSelected = selectedType?.subtype === 'instrument';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: 24,
          minWidth: 480,
          maxWidth: 600,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>创建连线</h3>
          <button
            onClick={handleCancel}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 20,
              cursor: 'pointer',
              color: '#999',
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Direction */}
        <div style={{ marginBottom: 20, fontSize: 13, color: '#666' }}>
          连线方向：<span style={{ color: '#1890ff' }}>{sourceLabel}</span>
          <span style={{ margin: '0 8px', color: '#999' }}>→</span>
          <span style={{ color: '#52c41a' }}>{targetLabel}</span>
        </div>

        {structuralTypes.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#333',
                marginBottom: 12,
              }}
            >
              结构关系：
            </div>
            <div
              style={{
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              {structuralTypes.map(renderOption)}
            </div>
          </div>
        )}

        {proceduralTypes.length > 0 && (
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#333',
                marginBottom: 12,
              }}
            >
              过程关系：
            </div>
            <div
              style={{
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              {proceduralTypes.map(renderOption)}
            </div>

            {isProcessSubGraph && isInstrumentSelected && (
              <div
                style={{
                  marginTop: 14,
                  border: '1px solid #e8e8e8',
                  borderRadius: 6,
                  padding: 12,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr auto',
                  gap: 10,
                  alignItems: 'end',
                }}
              >
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#595959' }}>
                  极性
                  <select
                    value={instrumentPolarity}
                    onChange={e => {
                      const value = e.target.value as InstrumentPolarity;
                      setInstrumentPolarity(value);
                      setSelectedType(prev =>
                        prev?.subtype === 'instrument'
                          ? { ...prev, instrumentPolarity: value }
                          : prev,
                      );
                    }}
                    style={{ height: 30, border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 8px' }}
                  >
                    <option value="none">None</option>
                    <option value="not">NOT</option>
                  </select>
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#595959' }}>
                  类型
                  <select
                    value={instrumentDisplayType}
                    onChange={e => {
                      const value = e.target.value as InstrumentDisplayType;
                      setInstrumentDisplayType(value);
                      setSelectedType(prev =>
                        prev?.subtype === 'instrument'
                          ? { ...prev, instrumentDisplayType: value }
                          : prev,
                      );
                    }}
                    style={{ height: 30, border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 8px' }}
                  >
                    <option value="instrument">instrument</option>
                    <option value="instrument-condition">instrument condition</option>
                    <option value="instrument-event">instrument Event</option>
                  </select>
                </label>

                <div
                  style={{
                    width: 92,
                    height: 48,
                    borderRadius: 6,
                    border: '1px solid #f0f0f0',
                    background: '#f7fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <InstrumentIcon polarity={instrumentPolarity} displayType={instrumentDisplayType} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 24,
          }}
        >
          <button
            onClick={handleCancel}
            style={{
              padding: '6px 16px',
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              background: '#fff',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedType}
            style={{
              padding: '6px 16px',
              border: 'none',
              borderRadius: 4,
              background: selectedType ? '#1890ff' : '#d9d9d9',
              color: '#fff',
              cursor: selectedType ? 'pointer' : 'not-allowed',
              fontSize: 13,
            }}
          >
            创建
          </button>
        </div>
      </div>
    </div>
  );
};

export default EdgeTypeDialog;
